import { useEffect, useRef } from 'react';
import { ELK_RELAYOUT_ON_DIMS_DEBOUNCE_MS } from './constants';
import { getConnectedLayoutTasks } from './isolatedSpawnPositions';

function dimensionsSignatureForTasks(nodeDimensions, layoutTasks) {
  if (!layoutTasks?.length) return '';
  return layoutTasks
    .map((t) => {
      const d = nodeDimensions[t.id];
      const w = d && Number.isFinite(d.width) ? Math.round(d.width) : 0;
      const h = d && Number.isFinite(d.height) ? Math.round(d.height) : 0;
      return `${t.id}:${w}x${h}`;
    })
    .sort()
    .join('|');
}

function allLayoutTasksMeasured(nodeDimensions, layoutTasks) {
  return layoutTasks.every((t) => {
    const d = nodeDimensions[t.id];
    return (
      d &&
      Number.isFinite(d.width) &&
      d.width > 0 &&
      Number.isFinite(d.height) &&
      d.height > 0
    );
  });
}

/**
 * Re-run ELK when measured node sizes change after initial post-measure. Incremental
 * vertical nudging fights DAG geometry and scrambles the board; a debounced ELK pass
 * keeps positions and edge routes coherent.
 *
 * Uses `layoutRequestModeRef = 'instant'` for chrome toggles. After a connect/delete/Tidy
 * hold, the follow-up is animated so it cannot snap over the in-flight slide.
 */
export function useElkRelayoutOnNodeDimensionsChange({
  postMeasureElkRelayoutDoneRef,
  taskIdSignature,
  nodeDimensions,
  isMultiCampaignMode,
  tasksForLayoutRef,
  nodeDimensionsRef,
  layoutRequestModeRef,
  setLayoutVersion,
  layoutVersion,
  layoutHoldRef = null,
  beginLayoutHold = null,
}) {
  const committedSigRef = useRef('');

  useEffect(() => {
    committedSigRef.current = '';
  }, [taskIdSignature]);

  useEffect(() => {
    if (!isMultiCampaignMode && !postMeasureElkRelayoutDoneRef.current) {
      return undefined;
    }

    const layoutTasks = tasksForLayoutRef.current;
    if (!layoutTasks?.length) return undefined;
    const connectedTasks = getConnectedLayoutTasks(layoutTasks);
    const measuredTasks = connectedTasks.length > 0 ? connectedTasks : layoutTasks;
    if (!allLayoutTasksMeasured(nodeDimensions, measuredTasks)) return undefined;

    const sig = dimensionsSignatureForTasks(nodeDimensions, measuredTasks);

    if (committedSigRef.current === '') {
      committedSigRef.current = sig;
      return undefined;
    }

    if (sig === committedSigRef.current) return undefined;

    let cancelled = false;
    let heldDuringWait = false;
    let id = window.setTimeout(function tryRelayout() {
      if (cancelled) return;
      if (layoutHoldRef?.current) {
        heldDuringWait = true;
        id = window.setTimeout(tryRelayout, ELK_RELAYOUT_ON_DIMS_DEBOUNCE_MS);
        return;
      }
      if (!isMultiCampaignMode && !postMeasureElkRelayoutDoneRef.current) return;
      const lt = tasksForLayoutRef.current;
      if (!lt?.length) return;
      const connected = getConnectedLayoutTasks(lt);
      const measured = connected.length > 0 ? connected : lt;
      const nd = nodeDimensionsRef.current;
      if (!allLayoutTasksMeasured(nd, measured)) return;
      const latest = dimensionsSignatureForTasks(nd, measured);
      if (latest === committedSigRef.current) return;
      committedSigRef.current = latest;
      // After a connect/delete/Tidy hold, animate so the follow-up cannot snap
      // over the slide. Chrome toggles outside a hold still snap.
      if (layoutRequestModeRef) {
        layoutRequestModeRef.current = heldDuringWait ? 'animate' : 'instant';
      }
      if (heldDuringWait) {
        beginLayoutHold?.();
      }
      setLayoutVersion((v) => v + 1);
    }, ELK_RELAYOUT_ON_DIMS_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [
    taskIdSignature,
    nodeDimensions,
    isMultiCampaignMode,
    tasksForLayoutRef,
    nodeDimensionsRef,
    postMeasureElkRelayoutDoneRef,
    layoutRequestModeRef,
    setLayoutVersion,
    layoutVersion,
    layoutHoldRef,
    beginLayoutHold,
  ]);
}
